import React, { FC, forwardRef, RefObject } from 'react';
import styled, { css } from 'styled-components';
import { createMarkup } from '../../../utils/createMarkup';
import { Typography } from '../../Typography';
import { components } from '../../../styles/tokens';
import { FormError } from './FormError';
// import { Html } from '../../Html';

export interface CheckboxProps {
  label?: string;
  id: string;
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: any;
  value?: string;
  disabled?: boolean;
  featured?: boolean;
  invalid?: boolean;
  type?: 'checkbox' | 'radio';
  name?: string;
  errorMessage?: string;
}

interface StyledCheckboxProps {
  $featured: boolean;
  $invalid: boolean;
}

const StyledCheckboxWrapper = styled.div<StyledCheckboxProps>`
  ${({ theme: { space, colors, borders, global, components }, $featured, $invalid }) => css`
    ${$featured &&
    css`
      padding: ${space.xl} ${space.lg};
      background-color: ${colors.primary.default};
      border-radius: ${global.borderRadius.default};
    `}
    label {
      display: flex;
      align-items: flex-start;
      position: relative;
      gap: ${space.sm};
      font-weight: 700;
      color: ${colors.primary.default};

      ${$featured &&
      css`
        color: ${colors.white.default};
        span {
          flex: 1 1 0;
        }
      `}

      ${$invalid &&
      css`
        color: ${components.global.invalid};
      `}
  
      &:before {
        content: '';
        width: 24px;
        height: 24px;
        border: ${components.checkbox.border} solid 2px;
        background: '#fff';
        transition: all 0.2s ease;
        border-radius: 8px;
        flex-shrink: 0;
      }

      &:after {
        content: '';
        width: 18px;
        height: 18px;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 32 32'%3E%3Cpath fill='currentColor' fill-rule='evenodd' d='m11.211 21.735 15.3-15.3c.58-.58 1.554-.58 2.133 0 .58.58.58 1.553 0 2.132L11.211 26 3.33 18.118a1.525 1.525 0 0 1 0-2.132c.58-.58 1.553-.58 2.132 0l5.75 5.749Z' clip-rule='evenodd'/%3E%3C/svg%3E");
        position: absolute;
        top: 3px;
        left: 3px;
        opacity: 0;
        transform: scale(0);
        transition: all 0.2s ease;

        ${$featured &&
        css`
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 13'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cpath d='M0-1h16v16H0z'/%3E%3Cpath fill='%23005AA0' d='M.234 7.336a.8.8 0 0 1 0-1.132l1.132-1.131a.8.8 0 0 1 1.131 0L6 8.576l7.503-7.503a.8.8 0 0 1 1.131 0l1.132 1.131a.8.8 0 0 1 0 1.132l-9.2 9.2a.8.8 0 0 1-1.132 0l-5.2-5.2Z'/%3E%3C/g%3E%3C/svg%3E%0A");
        `}
      }

      &:hover {
        cursor: pointer;
        &:before {
          border-color: ${colors.primary.default};
        }
      }
    }

    input {
      position: absolute;
      opacity: 0;
      cursor: pointer;
      height: 0;
      width: 0;

      &:checked + label {
        &:before {
          background: white;
          ${$featured &&
          css`
            background: ${colors.white.default};
          `}
        }

        &:after {
          opacity: 1;
          -webkit-transform: scale(1);
          transform: scale(1);
        }

        &:hover {
          border-color: ${colors.primary.default};
        }
      }
    }
  `};
`;

export const Checkbox: FC<React.PropsWithChildren<CheckboxProps>> = forwardRef(
  (
    {
      label,
      id,
      checked,
      defaultChecked,
      disabled,
      value,
      onChange,
      featured,
      children,
      name,
      invalid,
      errorMessage,
      type = 'checkbox',
      ...rest
    },
    ref
  ) => (
    <StyledCheckboxWrapper $featured={featured} {...rest} id={`checkbox-wrapper-${id}`} data-test="checkbox-wrapper">
      <input
        type={type}
        id={id}
        name={name || id}
        checked={checked}
        disabled={disabled}
        value={value}
        ref={ref as RefObject<HTMLInputElement>}
        onChange={onChange}
        defaultChecked={defaultChecked}
        invalid={invalid}
      />
      <label htmlFor={id} data-test="checkbox-label">
        <span>{children || createMarkup(label)}</span>
      </label>
      {invalid && (
        <FormError
          display={!!invalid && !!errorMessage}
          message={errorMessage || 'Required'}
        />
      )}
    </StyledCheckboxWrapper>
  )
);
